import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  UploadCloud, Loader2, CheckCircle2, AlertTriangle, Download, FileSpreadsheet,
  Copy, X,
} from 'lucide-react';
import { log } from '../../lib/logger';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only "Bulk Invite" modal. Upload a CSV or XLSX of users; every
 * successfully created row fires the same welcome email as single-user
 * invite. Returns a per-row result table so the admin can see exactly
 * which rows landed vs. which need attention.
 */
export const BulkInviteModal = ({ open, onClose, onCreated }) => {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const resetForm = () => {
    setFile(null); setErr(null); setResult(null); setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (submitting) return;
    const created = result?.created || 0;
    resetForm();
    onClose?.();
    if (created > 0) onCreated?.();
  };

  const handleFilePick = (f) => {
    setErr(null);
    setResult(null);
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith('.csv') && !name.endsWith('.xlsx') && !name.endsWith('.xlsm')) {
      setErr('Please choose a .csv or .xlsx file.');
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      setErr('File too large — 2 MB max.');
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFilePick(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || submitting) return;
    setErr(null);
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(
        `${API_URL}/api/users/admin-invite-bulk`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(r.data);
      if (r.data.created > 0) {
        toast.success(`${r.data.created} invitation${r.data.created === 1 ? '' : 's'} sent.`);
      }
      if (r.data.invalid > 0 || r.data.duplicate > 0) {
        toast.warning(
          `${r.data.invalid + r.data.duplicate} row(s) need attention — see the table below.`,
        );
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      log.error('Bulk invite failed:', error);
      setErr(typeof detail === 'string' ? detail : 'Could not process the file. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const r = await axios.get(
        `${API_URL}/api/users/admin-invite-bulk/template`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'user_invite_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      log.error('Template download failed:', error);
      toast.error('Could not download the template. Please try again.');
    }
  };

  const statusMeta = {
    created:   { Icon: CheckCircle2,  cls: 'bg-emerald-100 text-emerald-800', label: 'Created' },
    duplicate: { Icon: Copy,          cls: 'bg-amber-100 text-amber-800',    label: 'Duplicate' },
    invalid:   { Icon: AlertTriangle, cls: 'bg-red-100 text-red-800',        label: 'Invalid' },
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="bulk-invite-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#2F5D3A] flex items-center gap-2">
            <UploadCloud className="w-6 h-6" />
            Bulk Invite Users
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-1">
            Upload a CSV or Excel sheet with <span className="font-semibold">first_name</span>,{' '}
            <span className="font-semibold">last_name</span>, and{' '}
            <span className="font-semibold">email</span> columns. Each successful row gets the
            same welcome email as the single-user invite flow.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 mt-2" data-testid="bulk-invite-results">
            <div className="grid grid-cols-4 gap-3">
              <StatCard label="Total rows"  value={result.total}      tone="gray" testid="bulk-stat-total" />
              <StatCard label="Created"     value={result.created}    tone="emerald" testid="bulk-stat-created" />
              <StatCard label="Duplicates"  value={result.duplicate}  tone="amber" testid="bulk-stat-duplicate" />
              <StatCard label="Invalid"     value={result.invalid}    tone="red" testid="bulk-stat-invalid" />
            </div>

            {result.results?.length > 0 && (
              <div className="border rounded-md overflow-hidden" data-testid="bulk-results-table">
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3 font-semibold text-gray-700 w-14">Row</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">Email</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-700 w-32">Status</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.results.map((row) => {
                        const meta = statusMeta[row.status] || statusMeta.invalid;
                        const { Icon, cls, label } = meta;
                        return (
                          <tr key={`${row.row_num}-${row.email}`} className="border-t border-gray-100">
                            <td className="py-2 px-3 text-gray-600">{row.row_num}</td>
                            <td className="py-2 px-3 font-mono text-xs text-gray-800 break-all">{row.email || '—'}</td>
                            <td className="py-2 px-3">
                              <span
                                data-testid={`bulk-row-status-${row.row_num}`}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}
                              >
                                <Icon className="w-3 h-3" />{label}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-xs text-gray-600">{row.error || (row.status === 'created' ? 'Welcome email queued' : '')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { resetForm(); }}
                className="flex-1"
                data-testid="bulk-invite-upload-another-btn"
              >
                Upload another
              </Button>
              <Button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-[#4A7C59] hover:bg-[#3D6A4A] text-white"
                data-testid="bulk-invite-done-btn"
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="bulk-invite-form">
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#4A7C59] hover:text-[#2F5D3A] transition-colors"
              data-testid="bulk-invite-template-download"
            >
              <Download className="w-4 h-4" />
              Download CSV template
            </button>

            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-[#4A7C59] bg-green-50'
                  : 'border-gray-300 hover:border-[#4A7C59] hover:bg-green-50'
              }`}
              data-testid="bulk-invite-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => handleFilePick(e.target.files?.[0])}
                data-testid="bulk-invite-file-input"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3" data-testid="bulk-invite-selected-file">
                  <FileSpreadsheet className="w-8 h-8 text-[#4A7C59]" />
                  <div className="text-left">
                    <div className="font-semibold text-gray-800">{file.name}</div>
                    <div className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); resetForm(); }}
                    className="ml-3 text-gray-400 hover:text-red-500"
                    data-testid="bulk-invite-clear-file-btn"
                    aria-label="Remove file"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <>
                  <UploadCloud className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <div className="font-semibold text-gray-700">Click to browse or drag a file here</div>
                  <div className="text-xs text-gray-500 mt-1">.csv or .xlsx · up to 2 MB · max 500 rows</div>
                </>
              )}
            </div>

            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-3">
              <div className="font-semibold text-gray-700 mb-1">Column format:</div>
              <div>
                <span className="font-semibold">Required:</span> first_name, last_name, email
              </div>
              <div>
                <span className="font-semibold">Optional:</span> company, phone, pricing_tier
                (evergreen | stickeryou), is_admin (true | false)
              </div>
            </div>

            {err && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="bulk-invite-error">
                {err}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-gray-300"
                onClick={handleClose}
                disabled={submitting}
                data-testid="bulk-invite-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!file || submitting}
                className="flex-1 bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] hover:from-[#4A7C59] hover:to-[#2F5D3A] text-white font-semibold"
                data-testid="bulk-invite-submit"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>
                ) : 'Upload & Send Invites'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const StatCard = ({ label, value, tone, testid }) => {
  const palette = {
    gray:    'bg-gray-50 border-gray-200 text-gray-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
    red:     'bg-red-50 border-red-200 text-red-900',
  }[tone] || 'bg-gray-50 border-gray-200 text-gray-800';
  return (
    <div
      className={`rounded-md border px-3 py-2 ${palette}`}
      data-testid={testid}
    >
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
};

export default BulkInviteModal;
